/**
 * Connection point markers - Shows spheres at available connection points
 * when a module is selected in the scene.
 */

import { useState, useCallback, useRef } from "react";
import * as THREE from "three";
import { ThreeEvent } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import {
  useSelectedModuleInstanceId,
  useSelectedModuleRootLink,
  useSelectedModuleCanBeChild,
  useAvailableConnections,
  useInstances,
  usePendingPlacementModule,
  useFreeConnectionPoints,
  useConnectShelfTarget,
  useConnectModeActive,
  useViewerStore,
} from "../store";
import { useWebSocket } from "../WebSocket";
import { useIsMobile } from "../hooks";
import { useConnectionPointLogic } from "./useConnectionPointLogic";

// Marker configuration
const MARKER_RADIUS = 0.015;
const MARKER_SEGMENTS = 16;

// How much larger the invisible pick sphere is than the dot you see. A mouse
// pointer is a single pixel and needs almost no margin; a fingertip covers
// roughly 8mm of screen, so on touch the marker has to be a far bigger target
// than it looks.
const HIT_RADIUS_FACTOR_POINTER = 2.5;
const HIT_RADIUS_FACTOR_TOUCH = 6;
// The dot itself is also drawn larger on touch, so it can be seen and aimed at
// on a phone-sized viewport.
const VISIBLE_RADIUS_FACTOR_TOUCH = 1.6;

// Colors for available connection points
const COLORS = {
  available: "#ffc107", // amber/yellow
  availableHover: "#ffea00",
  // Free points shown while nothing is selected: dimmer, because they are an
  // invitation (long-press to choose a module) rather than the active flow.
  free: "#b28704",
  freeHover: "#ffc107",
};

// Same feel as the mesh long-press that opens the joint sheet.
const LONG_PRESS_DURATION_MS = 550;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

interface MarkerProps {
  position: [number, number, number];
  color: string;
  hoverColor: string;
  label: string;
  isTouch: boolean;
  onClick?: () => void;
  onHover?: (hovered: boolean) => void;
  /** Fired after a 550ms still press; when it fires, onClick is suppressed. */
  onLongPress?: () => void;
}

function Marker({ position, color, hoverColor, label, isTouch, onClick, onHover, onLongPress }: MarkerProps) {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<THREE.Mesh>(null);

  const handlePointerEnter = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      setHovered(true);
      document.body.style.cursor = "pointer";
      onHover?.(true);
    },
    [onHover]
  );

  const handlePointerLeave = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      setHovered(false);
      document.body.style.cursor = "default";
      onHover?.(false);
    },
    [onHover]
  );

  // Use onPointerDown instead of onClick to avoid timing issues
  // When hover triggers preview transforms, React re-renders can cause
  // the mesh to unmount/remount, which breaks onClick event registration.
  // onPointerDown fires immediately before any state updates can interfere.
  //
  // With a long-press action the immediate fire only happens when there is no
  // press to wait for: otherwise the click is delivered on release, so that a
  // finger held down opens the shelf instead of connecting.
  const handlePointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      if (!onLongPress) {
        onClick?.();
        return;
      }
      const start = { x: e.clientX, y: e.clientY };
      let done = false;
      const cleanup = () => {
        window.clearTimeout(timer);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointermove", handleMove);
      };
      const timer = window.setTimeout(() => {
        done = true;
        cleanup();
        onLongPress();
      }, LONG_PRESS_DURATION_MS);
      const handleMove = (moveEvent: PointerEvent) => {
        const travelled = Math.hypot(moveEvent.clientX - start.x, moveEvent.clientY - start.y);
        if (travelled > LONG_PRESS_MOVE_TOLERANCE_PX) {
          // The camera is being orbited, not the marker pressed.
          done = true;
          cleanup();
        }
      };
      const handleUp = () => {
        if (!done) {
          cleanup();
          onClick?.();
        }
      };
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointermove", handleMove);
    },
    [onClick, onLongPress]
  );

  const scale = hovered ? 1.3 : 1.0;
  const currentColor = hovered ? hoverColor : color;
  const hitRadius =
    MARKER_RADIUS * (isTouch ? HIT_RADIUS_FACTOR_TOUCH : HIT_RADIUS_FACTOR_POINTER);
  const visibleRadius = MARKER_RADIUS * (isTouch ? VISIBLE_RADIUS_FACTOR_TOUCH : 1);

  return (
    <group position={position}>
      {/* Invisible larger sphere for more reliable click detection */}
      <mesh
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDown}
      >
        <sphereGeometry args={[hitRadius, 8, 8]} />
        <meshBasicMaterial visible={false} />
      </mesh>
      {/* Visible marker sphere */}
      <mesh
        ref={meshRef}
        scale={[scale, scale, scale]}
      >
        <sphereGeometry args={[visibleRadius, MARKER_SEGMENTS, MARKER_SEGMENTS]} />
        <meshStandardMaterial
          color={currentColor}
          emissive={currentColor}
          emissiveIntensity={hovered ? 0.5 : 0.2}
          transparent
          opacity={0.9}
        />
      </mesh>
      {/* Tooltip on hover */}
      {hovered && (
        <Html
          position={[0, visibleRadius * 2, 0]}
          center
          style={{
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          <div
            style={{
              background: "rgba(0, 0, 0, 0.8)",
              color: "white",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontFamily: "monospace",
            }}
          >
            {label}
          </div>
        </Html>
      )}
    </group>
  );
}

export function ConnectionPointMarkers() {
  const selectedInstanceId = useSelectedModuleInstanceId();
  const rootLink = useSelectedModuleRootLink();
  const canBeChild = useSelectedModuleCanBeChild();
  const availableConnections = useAvailableConnections();
  const instances = useInstances();
  const isMobile = useIsMobile();
  const pendingPlacementModule = usePendingPlacementModule();
  const freeConnectionPoints = useFreeConnectionPoints();
  const connectShelfTarget = useConnectShelfTarget();
  const connectModeActive = useConnectModeActive();
  const openConnectShelf = useViewerStore((state) => state.openConnectShelf);
  const { sendConnectModules, sendPreviewConnection, sendSelectInstance } = useWebSocket();

  const {
    shouldRender,
    validConnections,
    handleClick,
    handleHover,
  } = useConnectionPointLogic({
    selectedInstanceId,
    rootLink,
    availableConnections,
    instances,
    canBeChild,
    actions: {
      sendConnectModules,
      sendPreviewConnection,
      sendSelectInstance,
    },
  });

  // While a module is armed, a tap means "put it here". Leaving the markers
  // live would make one tap both place the new module and connect the old
  // selection to that point. While the shelf is open, its ghost owns the
  // scene: more markers would only invite a second, conflicting flow.
  if (pendingPlacementModule || connectShelfTarget) {
    return null;
  }

  if (shouldRender) {
    return (
      <group>
        {/* Available connection points from other modules (clickable) */}
        {validConnections.map((conn) => (
          <Marker
            key={`available-${conn.instance_id}-${conn.link_name}`}
            position={conn.position}
            color={COLORS.available}
            hoverColor={COLORS.availableHover}
            label={`${conn.module_name} / ${conn.link_name}`}
            isTouch={isMobile}
            onClick={() => handleClick(conn.instance_id, conn.link_name)}
            onHover={(hovered) => handleHover(conn.instance_id, conn.link_name, hovered)}
            onLongPress={() => openConnectShelf(conn)}
          />
        ))}
      </group>
    );
  }

  // The free points only show while connect mode is armed - permanently
  // amber-studded robots read as noise, and the mode's whole promise is that
  // outside it the scene looks untouched. Inside it a plain tap opens the
  // shelf; the long-press still works for fingers trained on it.
  if (!connectModeActive || freeConnectionPoints.length === 0) {
    return null;
  }

  return (
    <group>
      {freeConnectionPoints.map((conn) => (
        <Marker
          key={`free-${conn.instance_id}-${conn.link_name}`}
          position={conn.position}
          color={COLORS.free}
          hoverColor={COLORS.freeHover}
          label={`${conn.module_name} / ${conn.link_name}`}
          isTouch={isMobile}
          onClick={() => openConnectShelf(conn)}
          onLongPress={() => openConnectShelf(conn)}
        />
      ))}
    </group>
  );
}
